// Native Telegram MainButton hook — Phase 12 Plan 12-02 (Wave 1 эталон).
//
// Primary CTA каждого экрана — НАТИВНАЯ Telegram MainButton (НЕ telegram-ui
// `<Button>`, НЕ floating-кнопка). UI-SPEC §6 / RESEARCH Pitfall 5.
//
// sdk-react 3.3.9 (Open Q3 resolution): в этой версии НЕТ React-компонента
// `<MainButton>`. Есть императивный namespace `mainButton` (re-export из
// @telegram-apps/sdk): mount() / setParams({ text, isEnabled, isVisible,
// isLoaderVisible }) / onClick(fn) → off. Никакого `<MainButton>` / `BottomButton`
// JSX-компонента (web-слух про rename относится к Bot API naming, не к sdk-react).
//
// Pitfall 5 (double-tap + listener-leak):
//   - один primary-источник на экран (этот хук — единственный вызов на экране);
//   - loading=true + isEnabled=false на время запроса (защита от double-tap);
//   - cleanup обработчика (off) + unmount на размонтировании экрана.
//
// Вне Telegram (vitest jsdom / локальный dev) SDK-вызовы недоступны —
// `isAvailable()` guard превращает их в no-op, экран рендерит свой fallback
// `data-testid`-button (см. screens) для тестов.

import { useEffect } from "react";

import { mainButton } from "@telegram-apps/sdk-react";

export interface UseMainButtonOptions {
  /** Текст кнопки (через i18next — UI-SPEC §8 копирайтинг). */
  text: string;
  /** Клик-обработчик. Меняется → effect перепривязывает listener. */
  onClick: () => void;
  /** Кнопка активна. false → серая, не кликается (disabled). */
  isEnabled?: boolean;
  /** Показывать ли кнопку вообще (по умолчанию true). */
  isVisible?: boolean;
  /** Спиннер внутри кнопки (на время async-запроса). */
  isLoading?: boolean;
}

/**
 * SafeWrapped-функции SDK имеют `.isAvailable()`. Вне Telegram (jsdom) она
 * вернёт false → no-op. Сужаем тип через unknown, не предполагая наличие метода.
 */
function isAvailable(fn: unknown): fn is ((...a: unknown[]) => unknown) & {
  isAvailable: () => boolean;
} {
  if (typeof fn !== "function") return false;
  const guard = (fn as { isAvailable?: unknown }).isAvailable;
  return typeof guard === "function" && (guard as () => boolean).call(fn);
}

function callIfAvailable(fn: unknown, ...args: unknown[]): void {
  if (isAvailable(fn)) {
    fn(...args);
  }
}

/**
 * Монтирует и синхронизирует нативную Telegram MainButton с переданным
 * состоянием. Idempotent mount; обработчик и видимость очищаются на unmount.
 *
 * Эталон для всех мигрируемых экранов (Wave 1/2). Бизнес-логику экрана НЕ
 * затрагивает — только презентация primary CTA.
 */
export function useMainButton({
  text,
  onClick,
  isEnabled = true,
  isVisible = true,
  isLoading = false,
}: UseMainButtonOptions): void {
  // Монтирование один раз за жизнь экрана; размонтирование + скрытие на unmount.
  useEffect(() => {
    callIfAvailable(mainButton.mount);
    return () => {
      // Скрыть, чтобы кнопка не «протекла» на следующий экран до его mount.
      callIfAvailable(mainButton.setParams, { isVisible: false });
      callIfAvailable(mainButton.unmount);
    };
  }, []);

  // Синхронизация визуальных параметров.
  useEffect(() => {
    callIfAvailable(mainButton.setParams, {
      text,
      isEnabled,
      isVisible,
      isLoaderVisible: isLoading,
    });
  }, [text, isEnabled, isVisible, isLoading]);

  // Привязка click-listener с cleanup (Pitfall 5 — listener leak guard).
  useEffect(() => {
    let off: VoidFunction | undefined;
    if (isAvailable(mainButton.onClick)) {
      off = (mainButton.onClick as (fn: () => void) => VoidFunction)(onClick);
    }
    return () => {
      off?.();
    };
  }, [onClick]);
}
